/**
 * SensitivityTornadoPage.tsx — Saltelli 2008 OAT sensitivity analysis
 *
 * For each input parameter, varies ±20% (configurable) and measures the
 * resulting change in module count, SOTE per metre and total AOTR.
 *
 * References:
 *   Saltelli, A. et al. (2008). Global Sensitivity Analysis: The Primer. Wiley.
 *   Morris, M.D. (1991). Factorial sampling plans for preliminary computational experiments.
 *
 * The "module count" output is the headline — the chart shows which inputs
 * the design is most sensitive to, telling the client which measurements
 * to prioritise during the post-award site programme.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { BarChart3, ArrowLeft, RefreshCcw } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, KEYS, onPersistChange } from '@/lib/sessionPersistence';
import {
  specFromLegacy, type LegacyModSpec,
  bubbleDiameterMm, sotePerMetre, moduleBedCoverageAreaM2,
} from '@/lib/diffuserPhysics';

const RESERVOIR_AREA_M2 = 34_000;

interface ModSpec extends LegacyModSpec {
  waterDepthM: number;
  mountingHeightM: number;
  waterTempC: number;
  alphaFactor?: number;
  sodKgM2D?: number;
  surfactantPenalty?: number;
}

const DEFAULTS: ModSpec = {
  diffuserCount: 8,
  diffuserType: 'membrane_disc_epdm',
  diffuserDiamMm: 300,
  poreSizeUm: 80,
  airflowPerDiffNm3h: 1.2,
  waterDepthM: 3.0,
  mountingHeightM: 0.40,
  waterTempC: 12,
  alphaFactor: 0.85,
  sodKgM2D: 0.6,
  surfactantPenalty: 0.6,
};

// Output metric: required module count given inputs
function moduleCount(s: ModSpec): number {
  const dSpec = specFromLegacy(s);
  const H_eff = Math.max(0.3, s.waterDepthM - s.mountingHeightM);
  const d_b = bubbleDiameterMm(dSpec, s.waterTempC);
  const sote = sotePerMetre(d_b) * (s.surfactantPenalty ?? 0.6);
  const coverage = moduleBedCoverageAreaM2(dSpec, H_eff) * 0.85;
  // Sizing driven by AOTR demand:
  const sodTotalKgH = (s.sodKgM2D ?? 0.6) * RESERVOIR_AREA_M2 / 24;
  const moduleAOTR = (s.airflowPerDiffNm3h * s.diffuserCount) * 1.293 * 0.2314 * (sote * H_eff / 100) * (s.alphaFactor ?? 0.85);
  const byOxygen = Math.ceil(sodTotalKgH / Math.max(0.001, moduleAOTR));
  const byCoverage = Math.ceil(RESERVOIR_AREA_M2 / Math.max(1, coverage));
  return Math.max(byOxygen, byCoverage);
}

interface Param {
  key: keyof ModSpec;
  label: string;
  unit: string;
  delta: number;          // fractional change (e.g. 0.2 = ±20%)
}

const PARAMS: Param[] = [
  { key: 'waterDepthM',         label: 'Water depth',          unit: 'm',    delta: 0.15 },
  { key: 'mountingHeightM',     label: 'Mounting height',      unit: 'm',    delta: 0.30 },
  { key: 'waterTempC',          label: 'Water temperature',    unit: '°C',   delta: 0.30 },
  { key: 'diffuserCount',       label: 'Diffusers per module', unit: '–',    delta: 0.25 },
  { key: 'diffuserDiamMm',      label: 'Diffuser diameter',    unit: 'mm',   delta: 0.20 },
  { key: 'poreSizeUm',          label: 'Pore size',            unit: 'µm',   delta: 0.30 },
  { key: 'airflowPerDiffNm3h',  label: 'Airflow per diffuser', unit: 'Nm³/h', delta: 0.25 },
  { key: 'alphaFactor',         label: 'α-factor (real water)', unit: '–',   delta: 0.20 },
  { key: 'sodKgM2D',            label: 'Sediment O₂ demand',   unit: 'kg/m²/d', delta: 0.35 },
  { key: 'surfactantPenalty',   label: 'Surfactant penalty f_contam', unit: '–', delta: 0.25 },
];

interface Bar {
  param: Param;
  base: number;
  low: number;
  high: number;
  range: number;
}

export default function SensitivityTornadoPage() {
  const navigate = useNavigate();
  const [spec, setSpec] = useState<ModSpec>(() => ({ ...DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS) }));
  const [globalDelta, setGlobalDelta] = useState(20);

  useEffect(() => {
    const unsub = onPersistChange(k => {
      if (k === KEYS.DIFFUSER_MODULE) {
        setSpec({ ...DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS) });
      }
    });
    return () => unsub();
  }, []);

  const baseCount = useMemo(() => moduleCount(spec), [spec]);

  const bars = useMemo<Bar[]>(() => {
    return PARAMS.map(p => {
      const baseVal = Number(spec[p.key] ?? 0);
      const dFrac = globalDelta / 100;
      const lowSpec  = { ...spec, [p.key]: baseVal * (1 - dFrac) };
      const highSpec = { ...spec, [p.key]: baseVal * (1 + dFrac) };
      const low = moduleCount(lowSpec);
      const high = moduleCount(highSpec);
      return { param: p, base: baseCount, low, high, range: Math.abs(high - low) };
    }).sort((a, b) => b.range - a.range);
  }, [spec, baseCount, globalDelta]);

  const maxRange = Math.max(...bars.map(b => Math.abs(b.high - b.base)), Math.max(...bars.map(b => Math.abs(b.low - b.base))), 1);
  const svgH = 30 + bars.length * 28;

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={BarChart3}
        title="Sensitivity Analysis — Module-Count Tornado"
        subtitle="One-at-a-time Saltelli OAT sensitivity. Identifies which inputs matter most for the sized module count."
        actions={
          <button onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-xl border border-rb-brand/30 bg-rb-brand/5 p-4">
          <div className="text-xs text-rb-brand uppercase tracking-wider">Baseline module count</div>
          <div className="text-3xl font-bold text-slate-100 mt-1">{baseCount}</div>
          <div className="text-xs text-slate-400 mt-1">at current active session</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Most sensitive input</div>
          <div className="text-xl font-bold text-amber-300 mt-1">{bars[0]?.param.label ?? '—'}</div>
          <div className="text-xs text-slate-500 mt-1">range {bars[0]?.low} – {bars[0]?.high} modules</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Least sensitive input</div>
          <div className="text-xl font-bold text-emerald-300 mt-1">{bars[bars.length - 1]?.param.label ?? '—'}</div>
          <div className="text-xs text-slate-500 mt-1">range {bars[bars.length - 1]?.low} – {bars[bars.length - 1]?.high} modules</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold text-slate-100">Perturbation magnitude</div>
          <button onClick={() => setSpec({ ...DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS) })}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs text-slate-300 hover:bg-slate-700">
            <RefreshCcw className="w-3.5 h-3.5" /> Reload session
          </button>
        </div>
        <div className="flex items-center gap-3">
          <input type="range" min={5} max={50} step={5} value={globalDelta}
            onChange={e => setGlobalDelta(Number(e.target.value))} className="flex-1" />
          <div className="w-16 text-right text-slate-100 font-semibold">±{globalDelta}%</div>
        </div>
      </div>

      {/* Tornado chart */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Module-count sensitivity (sorted by impact)</div>
        <svg viewBox={`0 0 720 ${svgH}`} className="w-full h-auto bg-slate-950 rounded">
          {(() => {
            const padL = 180;
            const plotW = 480;
            const centre = padL + plotW / 2;
            const scale = (plotW / 2) / maxRange;
            return (
              <>
                <line x1={centre} y1={10} x2={centre} y2={svgH - 5} stroke="#475569" strokeWidth={1} />
                <text x={centre} y={svgH - 2} textAnchor="middle" fontSize={9} fill="#94a3b8">baseline {baseCount}</text>
                {bars.map((b, i) => {
                  const yT = 16 + i * 28;
                  const lowDelta  = b.low  - baseCount;
                  const highDelta = b.high - baseCount;
                  const xLow  = centre + lowDelta * scale;
                  const xHigh = centre + highDelta * scale;
                  const xL = Math.min(xLow, xHigh);
                  const xR = Math.max(xLow, xHigh);
                  return (
                    <g key={i}>
                      <text x={padL - 8} y={yT + 12} textAnchor="end" fontSize={10} fill="#e2e8f0">{b.param.label}</text>
                      <rect x={xL} y={yT} width={Math.max(2, xR - xL)} height={18}
                        fill={i === 0 ? '#f59e0b' : '#475569'} opacity={0.85} />
                      <text x={xL - 4} y={yT + 12} textAnchor="end" fontSize={9} fill="#3b82f6">{b.low}</text>
                      <text x={xR + 4} y={yT + 12} fontSize={9} fill="#ef4444">{b.high}</text>
                    </g>
                  );
                })}
              </>
            );
          })()}
        </svg>
        <div className="text-[10px] text-slate-500 mt-2">Bar length = module-count range under ±{globalDelta}% perturbation. Wider bars = inputs that matter more.</div>
      </div>

      {/* Rationale */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Detail</div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-2 py-1.5 text-left text-slate-300">Input</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Base</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Low (−{globalDelta}%)</th>
                <th className="px-2 py-1.5 text-right text-slate-300">High (+{globalDelta}%)</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Range</th>
                <th className="px-2 py-1.5 text-right text-slate-300">% of base</th>
              </tr>
            </thead>
            <tbody>
              {bars.map((b, i) => (
                <tr key={i} className="border-b border-slate-800">
                  <td className="px-2 py-1 text-slate-100">{b.param.label}</td>
                  <td className="px-2 py-1 text-right text-slate-300 tabular-nums">{Number(spec[b.param.key] ?? 0).toFixed(2)} {b.param.unit}</td>
                  <td className="px-2 py-1 text-right text-blue-300 tabular-nums">{b.low}</td>
                  <td className="px-2 py-1 text-right text-red-300 tabular-nums">{b.high}</td>
                  <td className="px-2 py-1 text-right text-amber-300 tabular-nums font-semibold">{b.range}</td>
                  <td className="px-2 py-1 text-right text-slate-300 tabular-nums">{((b.range / Math.max(1, baseCount)) * 100).toFixed(0)} %</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">What to do with this</div>
        <div className="text-xs text-slate-400 leading-relaxed space-y-2">
          <p>
            The inputs at the top of the tornado are the ones that genuinely move the module count.
            They are the measurements to <strong>prioritise</strong> in the post-award site programme.
            Inputs at the bottom can be safely approximated with literature values.
          </p>
          <p>
            For example: if "sediment oxygen demand" dominates the tornado, the cost of getting
            site-measured SOD respirometry is justified — guessing it from textbook values exposes
            the design to material error. If "diffuser diameter" dominates, then the diffuser pilot
            selection matters more than the seasonal profile assumed.
          </p>
        </div>
      </div>
    </div>
  );
}
